import { Icon, KnownIcon } from 'common/Icon'
import { Link } from 'common/Link'
import { AppRoutes } from 'config/routes'
import { Account, AccountType } from 'graphql/types'
import { useStore } from 'lib/store'
import { ComponentProps, keyframes, styled } from 'lib/theme'
import { useRouter } from 'next/router'
import { Flex } from 'primitives/Flex'
import { List } from 'primitives/List'
import { ListItem } from 'primitives/ListItem'
import { Nav } from 'primitives/Nav'
import { Text } from 'primitives/Text'
import { useState } from 'react'
import { GradientNavLink } from '../GradientNSFWLink'

interface Link {
  name: string
  href: string
  icon: KnownIcon
  visible: boolean
}

const onlineFeedLinkCss = {
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  // Icon viewbox compensation
  // todo fix svgs
  marginLeft: '-10px'
}

const FeedLinkCss = {
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-start'
}

const LinkIconCss = {
  paddingRight: '10px'
}

const RefreshableFeedLink = () => {
  const { asPath } = useRouter()
  const [loading, setLoading] = useState(false)
  const store = useStore()
  const active = asPath.includes(AppRoutes.FEED)

  const handleOnClick = async () => {
    if (active) {
      setLoading(true)
      await store.feed.resetFeeds()
      setLoading(false)
    }
  }

  return (
    <Link
      icon
      css={FeedLinkCss}
      href={store.feed.currentFeedPath}
      onClick={handleOnClick}
      active={active}
    >
      <Icon icon="Home" css={LinkIconCss} />
      <Text type="body2" capitalize>
        Feed
      </Text>
      <Flex full css={{ justifyContent: 'end' }}>
        <Icon icon="Reload" spin={loading} iconSize={18} />
      </Flex>
    </Link>
  )
}

export interface FeedLinksMenuProps extends ComponentProps<typeof Nav> {
  user?: Account
}

export const FeedLinksMenu: React.FC<FeedLinksMenuProps> = ({ css, user }) => {
  const { asPath } = useRouter()

  return (
    <Nav
      css={{
        ...css
      }}
    >
      <List
        css={{
          '& li:not(:first-child)': {
            margin: '12px 0'
          }
        }}
      >
        <ListItem>
          <RefreshableFeedLink />
        </ListItem>
        {user && (
          <>
            <ListItem>
              <Link
                icon
                css={onlineFeedLinkCss}
                href={AppRoutes.PURCHASES}
                active={asPath === AppRoutes.PURCHASES}
              >
                <Icon icon="Purchases" css={LinkIconCss} />
                <Text type="body2" capitalize css={{ marginLeft: '-10px' }}>
                  Purchases
                </Text>
              </Link>
            </ListItem>
            <ListItem>
              <Link
                icon
                css={onlineFeedLinkCss}
                href={AppRoutes.ACTIVE_SUBS}
                active={asPath === AppRoutes.ACTIVE_SUBS || asPath === AppRoutes.EXPIRED_SUBS}
              >
                <Icon icon="Subscriptions" css={LinkIconCss} />
                <Text type="body2" capitalize css={{ marginLeft: '-11px' }}>
                  Subscriptions
                </Text>
              </Link>
            </ListItem>
          </>
        )}
        {user?.accountType === AccountType.CREATOR && (
          <>
            <ListItem>
              <Link
                icon
                css={FeedLinkCss}
                href={AppRoutes.EARNINGS}
                active={asPath === AppRoutes.EARNINGS}
              >
                <Icon icon="Wallet" css={LinkIconCss} />
                <Text type="body2" capitalize>
                  Earnings
                </Text>
              </Link>
            </ListItem>
            <ListItem>
              <Link
                icon
                css={onlineFeedLinkCss}
                href={AppRoutes.SALES}
                active={asPath === AppRoutes.SALES}
              >
                <Icon icon="Sales" css={LinkIconCss} />
                <Text type="body2" capitalize css={{ marginLeft: '-10px' }}>
                  Sales
                </Text>
              </Link>
            </ListItem>
            <ListItem>
              <Link
                icon
                css={onlineFeedLinkCss}
                href={AppRoutes.ANALYTICS}
                active={asPath === AppRoutes.ANALYTICS}
              >
                <Icon icon="Reports" css={LinkIconCss} />
                <Text type="body2" capitalize css={{ marginLeft: '-5px' }}>
                  Stats
                </Text>
              </Link>
            </ListItem>
            <ListItem>
              <Link
                icon
                css={FeedLinkCss}
                href={AppRoutes.SCHEDULED_POSTS}
                active={asPath === AppRoutes.SCHEDULED_POSTS}
              >
                <Icon icon="Scheduled" css={LinkIconCss} />
                <Text type="body2" capitalize css={{ marginLeft: '-10px' }}>
                  Scheduled
                </Text>
              </Link>
            </ListItem>
          </>
        )}
        {user && (
          <ListItem>
            <GradientNavLink
              icon
              css={FeedLinkCss}
              href={AppRoutes.REWARDS}
              active={asPath === AppRoutes.SCHEDULED_POSTS}
            >
              <Icon icon="AsteriskGradient" css={{ marginRight: 9 }} iconSize={22} />
              <Text type="body2" capitalize css={{ marginLeft: '0px' }}>
                Rewards
              </Text>
            </GradientNavLink>
          </ListItem>
        )}
      </List>
    </Nav>
  )
}
